import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import PriceHistorySection from '../components/PriceHistorySection';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { TrendingUp, TrendingDown, Target, Users, ShoppingCart, DollarSign, Plus, Trash2, Edit } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PeriodFilter } from '../components/PeriodFilter';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#4CAF50', '#2196F3', '#FF9800', '#E91E63', '#9C27B0', '#00BCD4', '#FF5722', '#607D8B'];

const formatCurrency = (value) => {
  return `RWF ${value?.toLocaleString() || 0}`;
};

const AnalyticsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Analytics data
  const [summary, setSummary] = useState(null);
  const [profitByCustomer, setProfitByCustomer] = useState([]);
  const [profitByCategory, setProfitByCategory] = useState([]);
  const [profitByProduct, setProfitByProduct] = useState([]);
  const [trends, setTrends] = useState([]);
  const [trendPeriod, setTrendPeriod] = useState('monthly');
  const [orderFrequency, setOrderFrequency] = useState([]);
  const [priceTrends, setPriceTrends] = useState([]);
  const [targets, setTargets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [periodFilter, setPeriodFilter] = useState({ startDate: '', endDate: '', label: '' });
  
  // Target form
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [targetForm, setTargetForm] = useState({
    customer_id: '',
    target_amount: '',
    period: 'monthly',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    quarter: 1
  });

  useEffect(() => {
    fetchAllData();
  }, [periodFilter]);

  useEffect(() => {
    fetchTrends();
  }, [trendPeriod]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const dateParams = new URLSearchParams();
      if (periodFilter.startDate) dateParams.append('start_date', periodFilter.startDate);
      if (periodFilter.endDate) dateParams.append('end_date', periodFilter.endDate);
      const qs = dateParams.toString() ? `?${dateParams.toString()}` : '';
      
      const [
        summaryRes,
        profitCustomerRes,
        profitCategoryRes,
        profitProductRes,
        frequencyRes,
        priceRes,
        targetsRes,
        customersRes
      ] = await Promise.all([
        axios.get(`${API_URL}/analytics/summary${qs}`),
        axios.get(`${API_URL}/analytics/profit-by-customer${qs}`),
        axios.get(`${API_URL}/analytics/profit-by-category${qs}`),
        axios.get(`${API_URL}/analytics/profit-by-product${qs}`),
        axios.get(`${API_URL}/analytics/order-frequency`),
        axios.get(`${API_URL}/analytics/price-trends`),
        axios.get(`${API_URL}/analytics/targets`),
        axios.get(`${API_URL}/customers`)
      ]);
      
      setSummary(summaryRes.data);
      setProfitByCustomer(profitCustomerRes.data);
      setProfitByCategory(profitCategoryRes.data);
      setProfitByProduct(profitProductRes.data);
      setOrderFrequency(frequencyRes.data);
      setPriceTrends(priceRes.data);
      setTargets(targetsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    try {
      const res = await axios.get(`${API_URL}/analytics/trends?period=${trendPeriod}`);
      setTrends(res.data);
    } catch (error) {
      console.error('Failed to fetch trends');
    }
  };

  const handleTargetSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...targetForm,
        target_amount: parseFloat(targetForm.target_amount)
      };
      
      if (editingTarget) {
        await axios.put(`${API_URL}/analytics/targets/${editingTarget.id}`, data);
        toast.success('Target updated');
      } else {
        await axios.post(`${API_URL}/analytics/targets`, data);
        toast.success('Target created');
      }
      
      setTargetDialogOpen(false);
      resetTargetForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDeleteTarget = async (id) => {
    if (!window.confirm('Delete this target?')) return;
    try {
      await axios.delete(`${API_URL}/analytics/targets/${id}`);
      toast.success('Target deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete target');
    }
  };

  const resetTargetForm = () => {
    setTargetForm({
      customer_id: '',
      target_amount: '',
      period: 'monthly',
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      quarter: 1
    });
    setEditingTarget(null);
  };

  const openEditTarget = (target) => {
    setEditingTarget(target);
    setTargetForm({
      customer_id: target.customer_id,
      target_amount: target.target_amount.toString(),
      period: target.period,
      year: target.year,
      month: target.month || 1,
      quarter: target.quarter || 1
    });
    setTargetDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'achieved': return 'text-green-600 bg-green-100';
      case 'on_track': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-red-600 bg-red-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <PriceHistorySection />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground">Data Analytics</h1>
          <p className="text-muted-foreground">Insights and performance tracking</p>
        </div>
      </div>

      {/* Period Filter */}
      <PeriodFilter onFilterChange={setPeriodFilter} />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <p className="text-2xl font-bold text-foreground">{formatCurrency(summary?.total_revenue)}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Gross Profit</p>
              <p className="text-2xl font-bold text-foreground">{formatCurrency(summary?.total_profit)}</p>
              <p className="text-xs text-muted-foreground">{summary?.overall_margin_percent}% margin</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Orders</p>
              <p className="text-2xl font-bold text-foreground">{summary?.total_orders?.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active Customers</p>
              <p className="text-2xl font-bold text-foreground">{summary?.unique_customers}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-2">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'customers', label: 'By Customer' },
          { id: 'categories', label: 'By Category' },
          { id: 'products', label: 'By Product' },
          { id: 'trends', label: 'Trends' },
          { id: 'frequency', label: 'Order Frequency' },
          { id: 'prices', label: 'Price Trends' },
          { id: 'targets', label: 'Targets' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-primary text-white'
                : 'bg-muted/50 text-muted-foreground hover:bg-muted'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white p-6 rounded-xl border border-border shadow-sm min-h-[400px]">
        
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Profit Overview</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Profit by Category Pie Chart */}
              <div>
                <h3 className="text-lg font-medium mb-4">Profit by Category</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={profitByCategory}
                      dataKey="gross_profit"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ category, percent }) => `${category} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {profitByCategory.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Top Customers Bar Chart */}
              <div>
                <h3 className="text-lg font-medium mb-4">Top 5 Customers by Profit</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={profitByCustomer.slice(0, 5)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} />
                    <YAxis type="category" dataKey="customer_name" width={120} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="gross_profit" fill="#4CAF50" name="Gross Profit" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Gross Profit by Customer</h2>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="profit-customer-table">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Customer</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Revenue</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">COGS</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Gross Profit</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Margin %</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Orders</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {profitByCustomer.map((item) => (
                    <tr key={item.customer_id || item.customer_name} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm font-medium">{item.customer_name}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(item.revenue)}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(item.cogs)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-green-600">{formatCurrency(item.gross_profit)}</td>
                      <td className="px-4 py-3 text-sm text-right">{item.margin_percent}%</td>
                      <td className="px-4 py-3 text-sm text-right">{item.orders_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Gross Profit by Category</h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={profitByCategory}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="revenue" fill="#2196F3" name="Revenue" />
                <Bar dataKey="gross_profit" fill="#4CAF50" name="Gross Profit" />
              </BarChart>
            </ResponsiveContainer>
            
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="profit-category-table">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Category</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Revenue</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">COGS</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Gross Profit</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Margin %</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Units Sold</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {profitByCategory.map((item) => (
                    <tr key={item.category} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm font-medium">{item.category}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(item.revenue)}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(item.cogs)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-green-600">{formatCurrency(item.gross_profit)}</td>
                      <td className="px-4 py-3 text-sm text-right">{item.margin_percent}%</td>
                      <td className="px-4 py-3 text-sm text-right">{item.units_sold}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Gross Profit by Product</h2>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full" data-testid="profit-product-table">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Product</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Category</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Revenue</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">COGS</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Gross Profit</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Margin %</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Units Sold</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {profitByProduct.map((item) => (
                    <tr key={item.product_id || item.product_name} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm font-medium">{item.product_name}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{item.category}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(item.revenue)}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(item.cogs)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-green-600">{formatCurrency(item.gross_profit)}</td>
                      <td className="px-4 py-3 text-sm text-right">{item.margin_percent}%</td>
                      <td className="px-4 py-3 text-sm text-right">{item.units_sold}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Trends Tab */}
        {activeTab === 'trends' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Revenue & Profit Trends</h2>
              <div className="flex gap-2">
                <Button
                  variant={trendPeriod === 'weekly' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTrendPeriod('weekly')}
                >
                  Weekly
                </Button>
                <Button
                  variant={trendPeriod === 'monthly' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTrendPeriod('monthly')}
                >
                  Monthly
                </Button>
              </div>
            </div>
            
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#2196F3" name="Revenue" strokeWidth={2} />
                <Line type="monotone" dataKey="gross_profit" stroke="#4CAF50" name="Gross Profit" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Period</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Revenue</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">COGS</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Gross Profit</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Margin %</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Orders</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {trends.map((item) => (
                    <tr key={item.period} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm font-medium">{item.period}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(item.revenue)}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(item.cogs)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-green-600">{formatCurrency(item.gross_profit)}</td>
                      <td className="px-4 py-3 text-sm text-right">{item.margin_percent}%</td>
                      <td className="px-4 py-3 text-sm text-right">{item.orders_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Order Frequency Tab */}
        {activeTab === 'frequency' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Order Frequency by Customer</h2>
            
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={orderFrequency.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="customer_name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="total_orders" fill="#4CAF50" name="Total Orders" />
                <Bar dataKey="orders_per_month" fill="#2196F3" name="Orders/Month" />
              </BarChart>
            </ResponsiveContainer>
            
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="order-frequency-table">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Customer</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Total Orders</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Orders/Month</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Avg Days Between</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">First Order</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Last Order</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {orderFrequency.map((item) => (
                    <tr key={item.customer_id || item.customer_name} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm font-medium">{item.customer_name}</td>
                      <td className="px-4 py-3 text-sm text-right">{item.total_orders}</td>
                      <td className="px-4 py-3 text-sm text-right">{item.orders_per_month}</td>
                      <td className="px-4 py-3 text-sm text-right">{item.avg_days_between_orders} days</td>
                      <td className="px-4 py-3 text-sm">{item.first_order?.split('T')[0] || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">{item.last_order?.split('T')[0] || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Price Trends Tab */}
        {activeTab === 'prices' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Price Trends (Purchase Costs)</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {priceTrends.slice(0, 6).map((item) => (
                <div key={item.product_id || item.product_name} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">{item.product_name}</h4>
                    <div className="flex items-center gap-2">
                      <span className={`flex items-center text-sm ${
                        item.trend_direction === 'up' ? 'text-red-600' : 
                        item.trend_direction === 'down' ? 'text-green-600' : 'text-gray-500'
                      }`}>
                        {item.trend_direction === 'up' ? <TrendingUp className="w-4 h-4 mr-1" /> : 
                         item.trend_direction === 'down' ? <TrendingDown className="w-4 h-4 mr-1" /> : null}
                        {item.price_change_percent > 0 ? '+' : ''}{item.price_change_percent}%
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{item.category}</p>
                  <div className="mt-2 space-y-1">
                    <p className="text-lg font-semibold">{formatCurrency(item.avg_price)}/unit avg</p>
                    <p className="text-xs text-muted-foreground">
                      Range: {formatCurrency(item.min_price)} - {formatCurrency(item.max_price)} ({item.purchase_count} purchases)
                    </p>
                    <p className="text-xs">
                      <span className="text-muted-foreground">Variance: </span>
                      <span className={item.variance_percent > 15 ? 'text-amber-600 font-medium' : ''}>
                        {item.variance_percent}%
                      </span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="price-trends-table">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Product</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Category</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">First Price</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Last Price</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Change</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Min</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Max</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Variance</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Purchases</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {priceTrends.map((item) => (
                    <tr key={item.product_id || item.product_name} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm font-medium">{item.product_name}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{item.category}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(item.first_price)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(item.last_price)}</td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${
                        item.trend_direction === 'up' ? 'text-red-600' : 
                        item.trend_direction === 'down' ? 'text-green-600' : 'text-muted-foreground'
                      }`}>
                        {item.trend_direction === 'up' ? '↑' : item.trend_direction === 'down' ? '↓' : '–'}
                        {item.price_change_percent > 0 ? '+' : ''}{item.price_change_percent}%
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-green-600">{formatCurrency(item.min_price)}</td>
                      <td className="px-4 py-3 text-sm text-right text-red-600">{formatCurrency(item.max_price)}</td>
                      <td className={`px-4 py-3 text-sm text-right ${item.variance_percent > 15 ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`}>
                        {item.variance_percent}%
                      </td>
                      <td className="px-4 py-3 text-sm text-center">{item.purchase_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Targets Tab */}
        {activeTab === 'targets' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Profit Targets</h2>
              {isAdmin && (
                <Dialog open={targetDialogOpen} onOpenChange={(open) => {
                  setTargetDialogOpen(open);
                  if (!open) resetTargetForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary text-white" data-testid="add-target-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Target
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="target-dialog">
                    <DialogHeader>
                      <DialogTitle>{editingTarget ? 'Edit Target' : 'Add Profit Target'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleTargetSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Customer</label>
                        <Select
                          value={targetForm.customer_id}
                          onValueChange={(value) => setTargetForm({ ...targetForm, customer_id: value })}
                        >
                          <SelectTrigger data-testid="target-customer-select">
                            <SelectValue placeholder="Select customer" />
                          </SelectTrigger>
                          <SelectContent>
                            {customers.map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Target Amount (RWF)</label>
                        <Input
                          type="number"
                          required
                          value={targetForm.target_amount}
                          onChange={(e) => setTargetForm({ ...targetForm, target_amount: e.target.value })}
                          placeholder="100000"
                          data-testid="target-amount-input"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Period</label>
                        <Select
                          value={targetForm.period}
                          onValueChange={(value) => setTargetForm({ ...targetForm, period: value })}
                        >
                          <SelectTrigger data-testid="target-period-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly</SelectItem>
                            <SelectItem value="yearly">Yearly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Year</label>
                          <Input
                            type="number"
                            required
                            value={targetForm.year}
                            onChange={(e) => setTargetForm({ ...targetForm, year: parseInt(e.target.value) })}
                            data-testid="target-year-input"
                          />
                        </div>
                        
                        {targetForm.period === 'monthly' && (
                          <div>
                            <label className="block text-sm font-medium mb-2">Month</label>
                            <Select
                              value={targetForm.month.toString()}
                              onValueChange={(value) => setTargetForm({ ...targetForm, month: parseInt(value) })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[...Array(12)].map((_, i) => (
                                  <SelectItem key={i+1} value={(i+1).toString()}>
                                    {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {targetForm.period === 'quarterly' && (
                          <div>
                            <label className="block text-sm font-medium mb-2">Quarter</label>
                            <Select
                              value={targetForm.quarter.toString()}
                              onValueChange={(value) => setTargetForm({ ...targetForm, quarter: parseInt(value) })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">Q1 (Jan-Mar)</SelectItem>
                                <SelectItem value="2">Q2 (Apr-Jun)</SelectItem>
                                <SelectItem value="3">Q3 (Jul-Sep)</SelectItem>
                                <SelectItem value="4">Q4 (Oct-Dec)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                      
                      <Button type="submit" className="w-full" data-testid="target-submit-btn">
                        {editingTarget ? 'Update Target' : 'Create Target'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            
            {targets.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No profit targets set yet.</p>
                {isAdmin && <p className="text-sm">Click "Add Target" to create one.</p>}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {targets.map((target) => (
                  <div key={target.id} className="p-4 border rounded-lg bg-white">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold">{target.customer_name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {target.period === 'monthly' && `${new Date(target.year, target.month - 1).toLocaleString('default', { month: 'short' })} ${target.year}`}
                          {target.period === 'quarterly' && `Q${target.quarter} ${target.year}`}
                          {target.period === 'yearly' && `${target.year}`}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(target.status)}`}>
                        {target.status === 'achieved' ? 'Achieved' : target.status === 'on_track' ? 'On Track' : 'Behind'}
                      </span>
                    </div>
                    
                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Progress</span>
                        <span>{target.progress_percent}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            target.status === 'achieved' ? 'bg-green-500' :
                            target.status === 'on_track' ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(100, target.progress_percent)}%` }}
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-between text-sm">
                      <div>
                        <p className="text-muted-foreground">Actual</p>
                        <p className="font-semibold">{formatCurrency(target.actual_profit)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-muted-foreground">Target</p>
                        <p className="font-semibold">{formatCurrency(target.target_amount)}</p>
                      </div>
                    </div>
                    
                    {isAdmin && (
                      <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
                        <Button variant="ghost" size="sm" onClick={() => openEditTarget(target)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteTarget(target.id)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
